/**
 * The chat dock, its rooms, its composer and its emoji picker
 * (components/chat/*, lib/chat.ts, and the names that come from @engineer-os/chat).
 *
 * See web/src/lib/strings/index.ts for how this file is shaped and why, and
 * docs/i18n.md for the Arabic glossary every value here must follow. Neither
 * object may be `as const`: `ar` is typed against `en`, and literal types
 * would make it have to repeat the English words.
 *
 * ## Register
 *
 * A room is a room. This is the one place in the product where learners talk
 * to each other rather than being taught, so the Arabic sits a notch below the
 * course prose: still Modern Standard Arabic, still second person, but the
 * contractions of manner a person actually uses — `أرسل`, `اذهب`, `لا شيء هنا
 * بعد` — rather than the register a translated interface drifts into. Nothing
 * of the grammar is contracted; `تقرأ فقط` is a sentence, not a fragment.
 *
 * ## The sentences that count things
 *
 * `panel.here` and `dock.toggle` are the reason this file has a `plural`
 * helper. Arabic has six categories to English's two and the dual is
 * obligatory, so "2 unread" is not "unread" with a 2 in front of it — it is a
 * different word, in a different case, with the numeral dropped, and "0 here"
 * is not a count at all but a negation. Every one of the six is written out
 * per language rather than assembled from a stem.
 *
 * The community half added six more of them — the people in a room the picker
 * is offering, the days it has been quiet, the pinned messages a room is
 * holding, the search results, the mentions waiting for you, and the unread in
 * rooms this tab is not in. Every one goes through `plural`. Two of those
 * sentences are said in more than one place, so they are functions above the
 * catalogue rather than values inside it: `hereEn`/`hereAr` and
 * `mineEn`/`mineAr`. A count written out twice is a count that drifts.
 *
 * ## What comes from the protocol instead
 *
 * A room's name and the two rules a person can break live in
 * @engineer-os/chat, because the Worker says them too and the panel must never
 * promise something the server then refuses. They take a language and default
 * to English, so what goes on the wire is unchanged and what the tab shows is
 * this catalogue's half. The custom emoji names are there for the same reason:
 * the roster is shared, so the Arabic name sits beside the English one in the
 * same row rather than in a second table that can fall out of step.
 */
import type { ReactNode } from 'react';
import {
  LIMITS,
  SUPPORT_LIMITS,
  clearedLine,
  customName,
  groupName,
  nameRule,
  roomLabel,
  searchRule,
  textRule,
  type CustomEmoji,
  type EmojiGroup,
  type RoomId,
  type SupportTopic,
} from '@engineer-os/chat';

import { ltr } from '../format';
import type { ImageRefusal } from '../image';

/* ── Arabic agreement ─────────────────────────────────────────────────────── */

interface Forms {
  /** Nothing. Arabic negates rather than counting: `لا أحد هنا`. */
  zero: string;
  /** One. The thing is named and the numeral is dropped: `شخص واحد هنا`. */
  one: string;
  /** Two. The dual, never `2` in front of a plural: `شخصان هنا`. */
  two: string;
  /** 3–10, where the counted noun is a plural in the genitive. */
  few: string;
  /** 11–99, where it goes back to an accusative singular: `15 شخصًا`. */
  many: string;
  /** 100, 101, 1000 … — the singular again, and the CLDR catch-all. */
  other: string;
}

/**
 * Pick the Arabic form a count agrees with. CLDR's rule for `ar`, all six of
 * it, because a chat panel can show any of them: a room of two is the dual, a
 * room of five is the plural, a room of ninety is the accusative singular, and
 * an empty one is not a count at all.
 */
function plural(n: number, forms: Forms): string {
  const rest = n % 100;
  if (n === 0) return forms.zero;
  if (n === 1) return forms.one;
  if (n === 2) return forms.two;
  if (rest >= 3 && rest <= 10) return forms.few;
  if (rest >= 11 && rest <= 99) return forms.many;
  return forms.other;
}

/* ── The two sentences that are said in more than one place ───────────────── */

/**
 * "12 here", about whichever room is being counted.
 *
 * One function per language rather than the same six forms written twice: the
 * header says this about the room on screen and the picker says it about every
 * room it offers, and two entries with the same words in them are two entries
 * that drift. The picker's is the whole point of the strip — a learner who
 * types a number into silence should have been told it was silent first.
 */
function hereEn(n: number): string {
  return `${n} here`;
}

function hereAr(n: number): string {
  return plural(n, {
    zero: 'لا أحد هنا',
    one: 'شخص واحد هنا',
    two: 'شخصان هنا',
    few: `${n} أشخاص هنا`,
    many: `${n} شخصًا هنا`,
    other: `${n} شخص هنا`,
  });
}

/**
 * "3 for you" — mentions waiting, said by the bubble in the corner and again
 * by the button in the header that opens them.
 *
 * Deliberately not the unread sentence. "Someone said something" and "someone
 * said something to *you*" are different news, and the corner has to be able
 * to carry both at once without either one reading as the other.
 */
function mineEn(n: number): string {
  return `${n} for you`;
}

function mineAr(n: number): string {
  return plural(n, {
    zero: 'لا إشارات إليك',
    one: 'إشارة واحدة إليك',
    two: 'إشارتان إليك',
    few: `${n} إشارات إليك`,
    many: `${n} إشارةً إليك`,
    other: `${n} إشارة إليك`,
  });
}

/* ── The toggle, which says the same thing twice ──────────────────────────── */

/**
 * What the bubble in the corner says.
 *
 * It is the accessible name of the button and, with the shortcut on the end,
 * the tooltip. Written once per language as a plain function so the two cannot
 * drift apart, and so that neither the label nor the tooltip is assembled out
 * of half-sentences at the call site.
 *
 * It now carries two counts rather than one, and they are listed rather than
 * added together. A learner with four unread messages and one that names them
 * has one piece of news that can wait and one that cannot, and a single number
 * that said "5" would have flattened the difference the badge exists to draw.
 * Either count may be zero, and a clause for a zero is not written at all —
 * "Open chat, 0 for you" is worse than saying nothing.
 */
function toggleEn(open: boolean, unread: number, mentions: number): string {
  if (open) return 'Close chat';
  const news: string[] = [];
  if (unread > 0) news.push(`${unread} unread`);
  if (mentions > 0) news.push(mineEn(mentions));
  return news.length > 0 ? `Open chat, ${news.join(', ')}` : 'Open chat';
}

function toggleAr(open: boolean, unread: number, mentions: number): string {
  if (open) return 'أغلق المحادثة';
  const news: string[] = [];
  if (unread > 0) {
    news.push(
      plural(unread, {
        zero: 'لا رسائل غير مقروءة',
        one: 'رسالة واحدة غير مقروءة',
        two: 'رسالتان غير مقروءتين',
        few: `${unread} رسائل غير مقروءة`,
        many: `${unread} رسالةً غير مقروءة`,
        other: `${unread} رسالة غير مقروءة`,
      }),
    );
  }
  if (mentions > 0) news.push(mineAr(mentions));
  // The Arabic comma, because this is a list inside a sentence rather than a
  // list of items in a table (docs/i18n.md §4).
  return news.length > 0 ? `افتح المحادثة، ${news.join('، ')}` : 'افتح المحادثة';
}

/** The key that opens it. Latin, isolated, and the same in both languages. */
const CHAT_SHORTCUT = ltr('Ctrl+/');

/* ── English ──────────────────────────────────────────────────────────────── */

export const en = {
  /* --- The panel's frame (ChatPanel.tsx) --------------------------------- */

  panel: {
    whoIsHere: 'Who is in this room',
    here: (n: number) => hereEn(n),
    /** While the line is down there is no number to be honest about. */
    hereUnknown: '—',
    close: 'Close chat',
    closeWhy: `Close chat (${ltr('Escape')})`,
    changeFace: 'Change your face',
    accountPicture: 'Your account picture',
    changeName: 'Change name',
  },

  /* --- The rooms this tab is holding (RoomStrip.tsx) --------------------- */

  strip: {
    label: 'Open rooms',
    /**
     * One tab's accessible name: which room, what is waiting in it, and how
     * much of that is addressed to the reader.
     *
     * The last clause is the reason this takes three arguments. The dot beside
     * the tab squares off when a mention is waiting, which is a fine
     * distinction for an eye and no distinction at all for a screen reader —
     * and being spoken to by name is precisely the news somebody navigating by
     * voice most needs. So the shape says it and the sentence says it.
     */
    tab: (room: RoomId, unread: number, mentions = 0) => {
      if (unread <= 0) return roomLabel(room, 'en');
      const waiting = `${roomLabel(room, 'en')}, ${unread} unread`;
      return mentions > 0 ? `${waiting}, ${mineEn(mentions)}` : waiting;
    },
    /** Why there are two of them at all. */
    why: 'The room you were in, and the room for the level you are standing in',
  },

  /* --- Where the people are (RoomPicker.tsx) ----------------------------- */

  occupancy: {
    /** People in the room: members, not listeners. A hundred silent sockets
     *  are not a hundred people, and saying so would be the same lie the
     *  empty room is made of. */
    here: (n: number) => hereEn(n),
    /** Nobody in it, and how long since anybody said anything. Day zero is
     *  today, which is a different sentence rather than "0 days". */
    quiet: (days: number) => (days < 1 ? 'empty, quiet today' : `empty, quiet ${days} days`),
    /** A room the index has never heard from at all. */
    never: 'empty',
  },

  /** The footer's one word for how the line is. Lower case on purpose: it is
   *  a state, not an announcement. */
  status: {
    listening: 'listening',
    connecting: 'connecting',
    roomFull: 'room full — retrying',
    offline: 'offline — retrying',
    /** The one state that is not going to fix itself. A socket the room shut
     *  on a ban reconnects into a closed door for ever, and "offline —
     *  retrying" would be a sentence that is technically true and completely
     *  misleading. The reason is whatever the deployment wrote, or nothing. */
    banned: (reason: string | null) => (reason ? `banned — ${reason}` : 'banned'),
  },

  /* --- The conversation (MessageList.tsx) -------------------------------- */

  list: {
    label: 'Messages',
    empty: 'Nothing here yet.',
    earlier: 'Earlier messages',
    loadingEarlier: 'Loading…',
    notSent: 'not sent',
    retry: 'Retry',
    /** The two days that have a name instead of a date. Every other day is
     *  `dayLabel` from lib/format, which is `Intl` in both languages. */
    today: 'Today',
    yesterday: 'Yesterday',
    /** A message whoever runs the deployment took down. The row stays, so a
     *  moderated conversation has a visible gap rather than quietly closing
     *  over one. */
    removed: 'Removed by whoever runs this deployment.',
    /** The same gap, left by the person who wrote it rather than by a
     *  moderator. Two sentences and not one, because the two are different
     *  facts about different people and drawing them alike would say that
     *  everybody who ever thought better of a line had been moderated. */
    deleted: 'Deleted by whoever wrote it.',
    /** The mark under a message whose author changed it after sending. Lower
     *  case and small: it is a footnote on somebody else's sentence, not an
     *  announcement. The time is in the `title`, for the reader who wants to
     *  know whether it changed before or after they answered it. */
    edited: 'edited',
    editedAt: (when: string) => `Edited at ${when}`,
    /** A message from somebody this browser is blocking. Collapsed, not gone:
     *  a block is a decision about noise, not about evidence. */
    blocked: 'Blocked',
    show: 'Show',
    hide: 'Hide',
    /** The mark on a message that is exempt from the room's window. */
    pinned: 'Pinned',
    /** The world posting into the room: "cleared Level 07", after a name. The
     *  protocol writes this sentence in both languages because the room stores
     *  the number and each end renders the words (packages/chat). */
    cleared: (level: number) => clearedLine(level, 'en'),
  },

  /* --- Where a message is typed (Composer.tsx) --------------------------- */

  composer: {
    message: 'Message',
    send: 'Send',
    sendWhy: `Send (${ltr('Enter')})`,
    emoji: 'Emoji',
    /** Two numbers and a slash. The same in both languages, and set left to
     *  right in both, because it is a measurement rather than a sentence. */
    count: (length: number, max: number) => `${length}/${max}`,
    /** While the field is holding a message that already exists. The verb
     *  changes with it: what this does is not send a message, and a button
     *  that still said Send would be offering to post the sentence twice. */
    save: 'Save',
    saveWhy: `Save the change (${ltr('Enter')})`,
    editing: 'Editing your message',
    cancelEdit: 'Stop editing',
    /** The paperclip. Three ways in — drop, paste, this — and this is the only
     *  one that works on a phone or from a keyboard alone. */
    attach: 'Add a picture',
    attachRemove: 'Take the picture off',
    attaching: 'Preparing…',
    attached: 'Ready to send',
    /**
     * Why a picture will not be sent, in the reader's own language.
     *
     * One sentence per reason rather than one for all of them, because each has
     * a different thing the person can do next: try a different file, come back
     * tomorrow, or nothing at all. "Upload failed" would be the same sentence
     * for all three and therefore useful for none.
     */
    refused: (reason: ImageRefusal): string =>
      reason === 'not-an-image'
        ? 'That is not a picture this browser can read.'
        : reason === 'too-many-pixels'
          ? 'That picture is too large to open here.'
          : reason === 'too-heavy'
            ? 'That picture is too heavy, even shrunk. Try a smaller one.'
            : reason === 'no-encoder'
              ? 'This browser cannot prepare a picture to send.'
              : reason === 'off'
                ? 'Pictures are not switched on here.'
                : reason === 'daily'
                  ? 'You have sent as many pictures as you can today.'
                  : reason === 'full'
                    ? 'There is no room for more pictures right now.'
                    : 'That did not reach the server. Try again.',
  },

  /* --- Answering one person (Composer.tsx, MessageList.tsx) -------------- */

  reply: {
    /** The bar above the field while you are answering somebody. The name is
     *  a person's own and may be in either script, so it arrives as an element
     *  that carries its own direction rather than as a word in a sentence. */
    to: (name: ReactNode) => ['Replying to ', name],
    cancel: 'Stop replying',
    /** The quote above a message that answers another one. */
    quote: 'Go to the message this answers',
  },

  mention: {
    /** The list that opens under an `@`. */
    label: 'People you can mention',
    /** On a run that names you, because the difference must survive greyscale
     *  and a tooltip is the cheapest way to say it in words as well. */
    you: 'This one is you',
  },

  /* --- A picture on a message (MessageList.tsx, ImageView.tsx) ----------- */

  image: {
    /**
     * The `alt` on every picture in a room, and — because the thumbnail is a
     * button with no label of its own — that button's accessible name too.
     *
     * There is no description to give. Nobody typed one, the composer never
     * asks for one, and a sentence invented here would describe a picture this
     * catalogue has not seen. `alt=""` is the other wrong answer: it means
     * "decoration, skip it", which is true of the figure beside a name
     * (Identity.tsx) and false of the only content a wordless message has.
     *
     * So it says the one thing that is both true and worth acting on — there
     * is a picture, and what is in it is written down nowhere. A reader can
     * then ask the room, which is the move an empty `alt` would have hidden.
     */
    alt: 'A picture nobody described',
    /** The `title` on the thumbnail. A description of the control, beside the
     *  `alt` that is the name of the thing — the arrangement `.msg-who` uses. */
    open: 'Open the picture at full size',
    /** The dialog the thumbnail opens. */
    full: 'The picture, full size',
    close: 'Close the picture',
    closeWhy: `Close the picture (${ltr('Escape')})`,
    /**
     * In place of the picture when the bytes do not arrive.
     *
     * Not an error and not an apology. An image can be swept, destroyed with
     * a report, or go with the account that posted it, and the message holding
     * the id outlives all three — so the ordinary end of a picture's life is a
     * 404, and the sentence says where it went rather than that something
     * broke.
     */
    gone: 'This picture is no longer here.',
  },

  /* --- Reacting (MessageList.tsx, MessageMenu.tsx) ----------------------- */

  reactions: {
    label: 'Reactions',
    add: 'Add a reaction',
    /** One bucket, said in words for a reader who cannot see the faces. */
    count: (n: number) => `${n} reacted`,
    /** The row is as wide as the protocol lets it get. Said before the send
     *  rather than after the refusal. */
    full: 'This message has all the reactions it can hold.',
  },

  /* --- What you can do with one message (MessageMenu.tsx) ---------------- */

  menu: {
    open: 'What you can do with this message',
    label: 'Message actions',
    react: 'React',
    reply: 'Reply',
    pin: 'Pin',
    unpin: 'Unpin',
    pinWhy: 'A pinned message stays when the rest of the room scrolls away',
    /** The server takes a pin from a signed-in member and nobody else, so the
     *  panel says why rather than offering a button that will be refused. */
    pinGated: 'Pinning is for signed-in people',
    /** The room will hold no more of them. */
    pinFull: 'This room is holding as many pinned messages as it can.',
    copy: 'Copy link',
    copied: 'Link copied.',
    /** Absent over plain HTTP and refused outright by some browsers. The link
     *  is then handed over selected, and the keyboard does what it always did. */
    copyManual: `Copy it with ${ltr('Ctrl+C')} — this browser would not let the page reach the clipboard.`,
    copyLabel: 'Link to this message',
    /** The two that exist only on your own messages, and are the mirror of
     *  Report and Block below: those are questions about somebody else, these
     *  are the only two things you may do to yourself. */
    edit: 'Edit',
    editWhy: 'The room shows that a message was edited, and when',
    delete: 'Delete',
    /** Said before the act, not after it. There is no undo and the title is
     *  the only place to say so where somebody will read it in time. */
    deleteWhy: 'The words go for everyone, and cannot be brought back',
    report: 'Report',
    block: 'Block',
    unblock: 'Unblock',
    blockWhy: 'Hides their messages in this browser. Nobody is told, and nothing is sent.',
  },

  /* --- What the room kept (PinnedStrip.tsx) ------------------------------ */

  pins: {
    count: (n: number) => `${n} pinned`,
    show: 'Show pinned messages',
    hide: 'Hide pinned messages',
    label: 'Pinned messages',
    open: 'Go to this message',
  },

  /* --- Finding something again (SearchBar.tsx) --------------------------- */

  search: {
    open: 'Search this room',
    close: 'Close search',
    placeholder: 'Search',
    go: 'Search',
    /** The protocol's own rule, in this language, on the control that is
     *  refusing — so the panel never promises a search the room will not run. */
    rule: () => searchRule('en'),
    busy: 'Searching…',
    /** The heading over the results. The query is the learner's own text and
     *  arrives as an element, because it may be in either script. */
    found: (n: number, q: ReactNode) => [`${n} found for “`, q, '”'],
    none: (q: ReactNode) => ['Nothing matches “', q, '”.'],
    back: 'Back to the conversation',
    /** One result. Clicking it leaves the results and lands in the room. */
    result: 'Show this in the conversation',
  },

  /* --- What was said to you (Inbox.tsx) ---------------------------------- */

  inbox: {
    /** The header's way in, and what it is holding. */
    open: (n: number) => mineEn(n),
    title: 'Mentions',
    label: 'People who said your name',
    empty: 'Nobody has said your name yet.',
    loading: 'Loading…',
    /** One row: who said it, and where they said it. */
    from: (name: ReactNode, room: RoomId) => [name, ' in ', roomLabel(room, 'en')],
    go: 'Open this message',
    markAll: 'Mark all as seen',
    /** The one switch in the chat that asks the browser for something. */
    notify: 'Notify me',
    notifyWhy: 'A browser notification when somebody names you. This browser only, and only while a tab is open.',
    /**
     * Rooms — not messages — with something in them this reader has not seen.
     *
     * It counts rooms because rooms is what can honestly be counted: the read
     * marks live beside the account and the message ids live inside each
     * room, and nothing anywhere knows how many messages a signed-out week
     * contained without waking every room to ask (lib/inbox.ts). Saying "12
     * unread" over a number that means "2 rooms" would be the sort of wrong
     * that is never found, because it always looks plausible.
     */
    elsewhere: (n: number) => (n === 1 ? '1 other room has something new' : `${n} other rooms have something new`),
    /** There is nowhere to keep a mention for a browser with no account. */
    signedOut: 'Mentions are kept with your account. Sign in and they follow you.',
    close: 'Close mentions',
  },

  /* --- Reporting one (ReportDialog.tsx) ---------------------------------- */

  report: {
    title: 'Report this message',
    /** The message being reported, quoted back so there is no doubt which. */
    quoted: 'The message',
    reason: 'What is wrong with it?',
    reasonNote: 'Optional. One sentence is enough.',
    send: 'Report',
    cancel: 'Cancel',
    /** Honest about where it goes. Nobody at EngineerOS reads these; whoever
     *  runs this deployment does, and they see everything below. */
    what: 'This goes to whoever runs this deployment. They see the message, your tag and what you write here. The person you are reporting is not told.',
    sent: 'Reported. Nothing else happens here.',
    close: 'Close',
  },

  /* --- Taking your own message back (DeleteDialog.tsx) ------------------- */

  remove: {
    title: 'Delete this message',
    /** Quoted back for the reason the report quotes it: the panel is small,
     *  the menu it was opened from has closed, and deleting the wrong line is
     *  the mistake this screen exists to prevent. */
    quoted: 'The message',
    /** What actually happens, in the order it happens. The tombstone is worth
     *  saying out loud — somebody who expects the row to disappear entirely
     *  should find that out here rather than afterwards. */
    what: 'The words go for everyone, here and in every reply that quoted them. A blank row stays where the message was, so the conversation around it still makes sense.',
    undo: 'This cannot be undone.',
    confirm: 'Delete',
    cancel: 'Cancel',
  },

  /* --- Who is here (MemberList.tsx) -------------------------------------- */

  members: {
    label: 'In this room',
    empty: 'Nobody here yet.',
  },

  /* --- Who somebody is (ProfileCard.tsx) --------------------------------- */

  who: {
    /** On every name that can be clicked: the member rows, the message heads. */
    open: 'Who this is',
    title: 'Who this is',
    back: 'Back to the conversation',
    /** In the member list this second. */
    here: 'In this room now',
    /** Not, so what is on screen came off a message rather than off the room. */
    gone: 'Not in this room now',
    goneNote: 'This is what they were showing the last time they wrote here.',
    /**
     * Which objective of their level they are standing in.
     *
     * Counted from one, and the wire counts from zero (`Member.objective`).
     * The conversion is at the call site rather than here, so that this stays
     * a sentence about a number somebody would say out loud: nobody is on
     * objective 0.
     */
    objective: (n: number) => `Working on objective ${n}`,
    /** Your own card. Clicking your own name is allowed rather than special. */
    you: 'This is you.',
    /** The one thing the card does that the message menu could not: address
     *  a person rather than act on one message. */
    mention: 'Mention them',
    mentionWhy: 'Puts their name in the box. It reaches their mentions, and waits there if they close the tab.',
    /**
     * Why it is refused for somebody who has left.
     *
     * Not a policy — a limit of where the tag comes from. What travels is a
     * tag and what is typed is a name, and the composer turns one into the
     * other by matching against the member list (components/chat/mentions.ts).
     * A name that matches nobody in the room is text, so the mention would be
     * written, look right, and address nobody. Said before the button rather
     * than discovered after the send.
     */
    mentionGone: 'They have to be in the room to be mentioned.',
    /* Blocking is on this card too, and its three words are deliberately not
       here: the card reads `menu.block`, `menu.unblock` and `menu.blockWhy`.
       It is one act with one sentence under it, and two copies of that
       sentence would be two sentences that drift — the same rule the counts
       at the top of this file follow. */
    /**
     * What this card is, said plainly.
     *
     * Every line on it came off the socket, which means it came from the tab
     * that person is sitting at: a level and a rank are what their browser
     * said about itself. Grading runs in the page (docs/cheating.md), so the
     * only half of this the server vouches for is the name, and only where
     * the check beside it says it is an account's. A card that implied the
     * rest was checked would be the first screen in this product to make a
     * claim it cannot back, and it would make one about somebody else.
     */
    what: 'What the room was told. The name is the account’s; the level and the rank are what their browser said.',

    /* --- The record behind the tag (lib/whois.ts) ------------------------ */

    /** The heading over the half that came from the server rather than the room. */
    record: 'Their record',
    /** While the request is out. One word: the card is already drawn around it. */
    loading: 'Loading…',
    /**
     * Levels cleared and stars, as that account's own record claims them.
     *
     * Two sentences and not one line of `3 · 9`, because the numbers mean
     * different things and a reader who has never seen this card before should
     * not have to work out which is which from their size.
     */
    cleared: (n: number) => (n === 1 ? '1 level cleared' : `${n} levels cleared`),
    stars: (n: number) => (n === 1 ? '1 star' : `${n} stars`),
    /** The two the server actually knows. `date` arrives formatted. */
    joined: (date: string) => `Joined ${date}`,
    seen: (date: string) => `Last here ${date}`,
    /**
     * An account with no pushed record, which is not the same as a new one:
     * a learner who chats here and plays on a laptop they never signed in on
     * has an account, a face, and nothing synced.
     */
    noRecord: 'Nothing synced to this account yet.',
    /**
     * There is no account behind this name, so there is no record and there
     * never could be.
     *
     * The route cannot tell this apart from an account with no progress — both
     * are 404, deliberately, so that probing learns nothing — but the *tab*
     * can, because `verified` rides on every member and every message and says
     * whether the room vouches for the name. Without this the card told
     * somebody to sign in to see a record that does not exist, which is the
     * worst of the three sentences it could say: it blames the reader for an
     * absence that is nothing to do with them.
     */
    noAccount: 'This name is a browser’s, not an account’s — there is no record to keep.',
    /**
     * The line that keeps the two halves apart, and the reason the record is
     * split from the dates above it at all.
     *
     * Said once, under the numbers it is about. The dates are the server's and
     * are true; these three are what that learner's browser pushed, and
     * grading runs in the page, so they are a claim. Stating it is cheaper
     * than the alternative — a learner reading another learner's count as a
     * verified score, in a product that ships `?unlock=all`.
     */
    claim: 'Counted from what their browser saved. Nothing here is checked.',
    /** Signed out, the record half is not fetched at all and this says why. */
    signedOut: 'Sign in to see what someone has done.',
    /**
     * Out of the panel and onto a page of their own (`/u/<tag>`).
     *
     * Offered on every card for an account, including a card with an empty
     * record on it: the public page is the half of this that anybody can open,
     * and a reader who wants to send somebody's record to a third person needs
     * a link rather than a screenshot of a chat panel.
     *
     * It says "confirmed" rather than "full", because it is not this card with
     * more on it — it is a smaller page with only the part a server checked
     * (components/profile/PublicProfile.tsx).
     */
    page: 'Confirmed record',
    pageWhy: 'Opens their public page in a new tab. It shows only the completions a server checked.',
  },

  /* --- Teams (Teams.tsx, docs/teams.md) ---------------------------------- */

  teams: {
    /** The header button, and what it is holding. */
    open: (n: number) => (n > 0 ? (n === 1 ? 'Teams, 1 invitation' : `Teams, ${n} invitations`) : 'Teams'),
    title: 'Teams',
    /** The block of invitations, above everything, because somebody is waiting. */
    asked: 'Asked to join',
    mine: 'Your teams',
    none: 'You are in no teams yet.',
    loading: 'Loading…',
    /** How many people are in one. Said about a team you are in and a team you
     *  are being asked to join, so it is one sentence rather than two. */
    size: (n: number) => (n === 1 ? '1 person' : `${n} people`),
    /** The two answers to an invitation, deliberately the same weight. */
    join: 'Join',
    decline: 'Decline',
    /** Who may invite. A word beside a name, not a badge. */
    lead: 'lead',
    youLead: 'You lead this team.',
    leave: 'Leave',
    new: 'New team',
    make: 'Create',
    cancel: 'Cancel',
    nameLabel: 'Call it something',
    /** From the profile card: the one action that starts a team relationship. */
    invite: 'Invite to a team',
    inviteWhy: 'Asks them to join. They decide, and nothing happens until they do.',
    /** Picking which of your teams to ask them into. */
    inviteTo: 'Invite to which team?',
    invited: 'Asked. They will see it next time they are here.',
    /** No team to invite anybody into yet, said where the button would be. */
    inviteNoTeam: 'Make a team first, then ask people into it.',
    /**
     * Said where the button would be for a name with no account behind it.
     *
     * This used to be silence: the whole block was gated on `isAccount`, so the
     * commonest case in any room with history — most names in it are browsers'
     * — showed nothing at all, and a feature that renders nothing is a feature
     * that has not been built, as far as the person looking at it can tell. It
     * is the same fact the record section states two paragraphs up, but that
     * sentence is about a record and nobody reads it as an answer to "why can
     * I not ask this person into my team".
     */
    inviteNoAccount: 'Only an account can be asked into a team — this name is a browser’s.',
    signedOut: 'Teams are kept with your account. Sign in to make one.',

    /* The Worker's refusals, in this language. The routes answer with a word
       from a closed set and `reason()` in Teams.tsx maps them; anything it
       does not recognise falls back to `failed`. */
    tooMany: (() => `You can be in ${LIMITS.team.perLearner} teams at once.`)(),
    teamFull: (() => `A team holds ${LIMITS.team.size} people.`)(),
    alreadyIn: 'They are already in it.',
    alreadyAsked: 'They have already been asked.',
    notLead: 'Only a team’s lead can invite people.',
    badName: (() => `A team’s name is ${LIMITS.team.name.min} to ${LIMITS.team.name.max} characters.`)(),
    failed: 'That did not work. Try again in a moment.',

    /**
     * The sentence this feature exists or does not exist on.
     *
     * A team room is the first room in the product that looks private, and it
     * is private from other *learners* — not from whoever runs the deployment,
     * who holds a token that can purge any room and hide any message. Saying
     * so in the panel rather than only in the docs is the whole reason this
     * was shippable: the moderation path is what makes a private room
     * defensible, and a room that looked sealed while being readable is
     * exactly where somebody says the thing they would not have said.
     */
    privacy: 'Only your team can read this room. Whoever runs EngineerOS can, too — that is how anything reported in here gets dealt with.',
  },

  /* --- Support (Support.tsx, docs/support.md) ---------------------------- */

  support: {
    /**
     * The word for this kind of conversation, on the button that opens it.
     *
     * `roomLabel` in @engineer-os/chat prints the same word over the room
     * itself, and the two have to stay in step or the panel calls one thing
     * by two names. It is not delegated to the way `rooms.label` is,
     * because that function takes a room and this button has no room yet —
     * and manufacturing a thread id to ask about would be a stranger thing
     * than keeping one word aligned by hand.
     */
    title: 'Support',
    /**
     * The header button's *name*, which is not the same as its face.
     *
     * The button draws `title` and, when there is news, a 6px mark beside it.
     * This is what it is called: the `aria-label` a screen reader reads and
     * the tooltip a pointer uncovers, so the news is a sentence for anybody
     * who asks and a dot for everybody who is only glancing. Drawing the
     * sentence is what it used to do, and it cost 217px of a bar that has
     * 305 of a 360px panel already spent — the close button went off the
     * card (chat.css, `.chat-support-dot`).
     *
     * `reply` means the last thing said was not yours, and the thread is
     * still open.
     */
    open: (reply: boolean): string => (reply ? 'Support, a reply is waiting' : 'Support'),
    signedOut: 'A conversation with support is kept with your account. Sign in to start one.',
    /**
     * Said for four different reasons — the feature is off, accounts are not
     * configured, the session went, or nobody is on the roster — and
     * deliberately the same sentence for all four. The learner's next move
     * is identical in every case, and "the support roster is empty" is our
     * problem described in our vocabulary.
     */
    closed: 'Nobody is taking questions here at the moment.',
    loading: 'Loading…',
    /**
     * How many people could pick this up right now.
     *
     * Zero is the case worth writing carefully. It is a true and
     * discouraging fact, and the sentence has to carry the part that makes
     * it survivable — the question waits, and somebody reads it — or a
     * learner with a charged card and nobody at the desk simply closes the
     * panel.
     */
    here: (n: number) =>
      n === 0
        ? 'Nobody is answering right now. Leave the question anyway — it will be read.'
        : n === 1
          ? '1 person is answering questions right now.'
          : `${n} people are answering questions right now.`,

    /* Starting one. */
    about: 'What is it about?',
    /**
     * The six, worded for the person asking rather than for the person
     * answering. The protocol's names (`access`, `billing`, `tasks`,
     * `content`, `bug`, `other`) are the desk's vocabulary and are what the queue is
     * grouped by; these are what a learner recognises their own situation
     * in, which is a different job and is why they are not the same words.
     */
    topics: {
      access: 'Getting in',
      billing: 'Payment',
      tasks: 'Paid tasks and AI credits',
      content: 'A level is wrong',
      bug: 'Something is broken',
      other: 'Something else',
    } satisfies Record<SupportTopic, string>,
    subjectLabel: 'One line about it, if you like',
    start: 'Start the conversation',

    /* The one you have going. */
    yours: 'Your conversation',
    stateOpen: 'open',
    stateResolved: 'closed',
    waiting: 'Nobody has answered yet.',
    replied: 'There is an answer waiting in it.',
    /** The agent's roster display name, which is theirs and takes its own direction. */
    withAgent: (name: ReactNode) => [name, ' is looking after this one.'],
    unclaimed: 'Nobody has picked it up yet.',
    opened: (date: string) => `Opened ${date}`,
    openRoom: 'Open the conversation',
    /** Why this is `chat.join` and not `chat.show`, said to the learner. */
    openRoomWhy: 'Opens it in the panel and keeps it there while you move around the course.',
    resolveIt: 'I am done here',
    resolveWhy: 'Closes it. Writing in it again opens it back up.',
    past: 'Closed conversations',

    /* The refusals. `openSupport` tells three apart and each one gets its own
       sentence, because the reader's next move differs for each and
       "something went wrong" is the sentence that makes somebody give up. */
    busy: (() => `You can start ${SUPPORT_LIMITS.perDay} conversations a day. Try again tomorrow.`)(),
    failed: 'That did not work. Try again in a moment.',

    /**
     * The sentence the whole feature stands on, and the reason it is allowed
     * to exist at all.
     *
     * Last in the popover, under everything it is about, and never behind a
     * disclosure — the `teams.privacy` precedent, for a stronger version of
     * the same situation. A team room is read by an operator who *could*
     * look; a support thread is read by one who certainly will, because
     * reading it is how it gets answered. Somebody about to paste an order
     * number, a screenshot of a bank app or a description of what is wrong
     * with their laptop is entitled to know that before they paste it, not
     * in a document they will never open.
     */
    privacy: 'Whoever runs EngineerOS reads everything in this conversation — they are the ones who answer it. No other learner can see it.',
  },

  /* --- Which room (RoomPicker.tsx) --------------------------------------- */

  rooms: {
    /** "Room 42", "Level 07" — the protocol's, so the picker and the header
     *  cannot disagree with the Worker about what a room is called. */
    label: (room: RoomId) => roomLabel(room, 'en'),
    change: 'Change room',
    follows: 'follows',
    followsWhy: 'This room follows the level you are on',
    number: 'Room',
    /** The placeholder in the number field: a range, so it stays as digits. */
    range: (min: number, max: number) => `${min}–${max}`,
    go: 'Go',
    thisLevel: 'This level',
    thisLevelWhy: 'Follow the level you are on',
    recent: 'Recent',
  },

  /* --- Picking a name (NamePrompt.tsx) ----------------------------------- */

  name: {
    pick: 'Pick a name',
    change: 'Change name',
    join: 'Join',
    joinRoom: (room: RoomId) => `Join ${roomLabel(room, 'en')}`,
    save: 'Save',
    cancel: 'Cancel',
    inlineNote: 'Pick a name to join in. Until then you are only reading.',
    note: 'Shown beside what you write, with a tag only this browser has. No account.',
  },

  /* --- Signing in (SignInPrompt.tsx, Identity.tsx) ----------------------- */

  signIn: {
    title: 'Sign in to post',
    note: (room: RoomId) =>
      `You are reading ${roomLabel(room, 'en')}. Signing in gives you one name and one tag in every room, on every device.`,
    button: 'Sign in',
    busy: 'Signing in…',
    why: 'Post under your account, here and on any other browser',
    /** The check beside a name, and what a screen reader says for it. */
    verified: 'Signed in to an account',
    verifiedShort: 'signed in',
  },

  /* --- Which face (FacePicker.tsx) --------------------------------------- */

  faces: {
    label: 'Faces',
    byDefault: 'Yours by default',
    useDefault: (face: string) => `Use the one my tag gives me (${face})`,
  },

  /* --- The palette (EmojiPicker.tsx) ------------------------------------- */

  emoji: {
    search: 'Search',
    searchLabel: 'Search emoji',
    groups: 'Groups',
    /** Two headings the picker makes up, beside the ones the tables carry. */
    results: 'Results',
    recent: 'Recent',
    none: 'Nothing matches that.',
    /** A group heading, from whichever table it came out of. */
    group: (g: EmojiGroup) => groupName(g, 'en'),
    /** One of ours, for the screen reader. */
    name: (e: CustomEmoji) => customName(e, 'en'),
    /** The tooltip on one of ours: what it is called, then what it is typed
     *  as. The shortcode is isolated, or bidi walks the colons off it. */
    cell: (e: CustomEmoji) => `${customName(e, 'en')} — ${ltr(e.c)}`,
  },

  /* --- The dock itself (ChatDock.tsx) ------------------------------------ */

  dock: {
    /** The button's accessible name: what pressing it does, and what is
     *  waiting behind it — messages, and the ones that name you. */
    toggle: (open: boolean, unread: number, mentions: number) => toggleEn(open, unread, mentions),
    /** The same sentence with the key on the end, for the tooltip. */
    toggleWhy: (open: boolean, unread: number, mentions: number) => `${toggleEn(open, unread, mentions)} (${CHAT_SHORTCUT})`,
    panel: 'Chat',
    width: 'Chat width',
    height: 'Chat height',
    resize: 'Drag to resize',
    resizeReset: `Drag to resize · ${ltr('Home')} to reset`,
  },

  /* --- The two rules a person can break ---------------------------------- */

  /**
   * The protocol's sentences, asked for in this language. The room says the
   * English ones over the socket; the tab shows these instead, so a learner
   * reading Arabic is not handed a rule in English at the one moment they
   * have got something wrong.
   */
  rules: {
    name: () => nameRule('en'),
    text: () => textRule('en'),
    /** The room refused an edit or a delete because the message is not this
     *  socket's to change. Almost always a tab that was open across a sign-in:
     *  the same person, a different tag, and only the room can know. */
    notYours: 'You can only change a message you sent from this browser.',
  },
};

/* ── Arabic ───────────────────────────────────────────────────────────────── */

export const ar: typeof en = {
  panel: {
    whoIsHere: 'من في هذه الغرفة',
    here: (n: number) => hereAr(n),
    hereUnknown: '—',
    close: 'أغلق المحادثة',
    closeWhy: `أغلق المحادثة (${ltr('Escape')})`,
    changeFace: 'غيّر وجهك',
    accountPicture: 'صورة حسابك',
    changeName: 'غيّر الاسم',
  },

  strip: {
    label: 'الغرف المفتوحة',
    tab: (room: RoomId, unread: number, mentions = 0) => {
      if (unread <= 0) return roomLabel(room, 'ar');
      const waiting = `${roomLabel(room, 'ar')}، ${plural(unread, {
        zero: 'لا رسائل غير مقروءة',
        one: 'رسالة واحدة غير مقروءة',
        two: 'رسالتان غير مقروءتين',
        few: `${unread} رسائل غير مقروءة`,
        many: `${unread} رسالةً غير مقروءة`,
        other: `${unread} رسالة غير مقروءة`,
      })}`;
      return mentions > 0 ? `${waiting}، ${mineAr(mentions)}` : waiting;
    },
    why: 'الغرفة التي كنت فيها، وغرفة المستوى الذي تقف فيه الآن',
  },

  occupancy: {
    here: (n: number) => hereAr(n),
    quiet: (days: number) =>
      plural(days, {
        zero: 'فارغة، هادئة اليوم',
        one: 'فارغة، هادئة منذ يوم',
        two: 'فارغة، هادئة منذ يومين',
        few: `فارغة، هادئة منذ ${days} أيام`,
        many: `فارغة، هادئة منذ ${days} يومًا`,
        other: `فارغة، هادئة منذ ${days} يوم`,
      }),
    never: 'فارغة',
  },

  status: {
    // Not a word for word "listening": in Arabic the honest description of a
    // socket that receives everything and posts nothing is that you are
    // reading, and that is also what the prompt above it offers to end.
    listening: 'تقرأ فقط',
    connecting: 'جارٍ الاتصال',
    roomFull: 'الغرفة ممتلئة — تُعاد المحاولة',
    offline: 'غير متصل — تُعاد المحاولة',
    banned: (reason: string | null) => (reason ? `محظور — ${reason}` : 'محظور'),
  },

  list: {
    label: 'الرسائل',
    empty: 'لا توجد رسائل بعد.',
    earlier: 'رسائل أقدم',
    loadingEarlier: 'جارٍ التحميل…',
    notSent: 'لم تُرسل',
    retry: 'أعد المحاولة',
    today: 'اليوم',
    yesterday: 'أمس',
    removed: 'أزالها من يدير هذا الموقع.',
    deleted: 'حذفها كاتبها.',
    edited: 'مُعدَّلة',
    editedAt: (when: string) => `عُدِّلت في ${when}`,
    blocked: 'محظور',
    show: 'أظهر',
    hide: 'أخفِ',
    pinned: 'مثبتة',
    cleared: (level: number) => clearedLine(level, 'ar'),
  },

  composer: {
    message: 'رسالة',
    send: 'أرسل',
    sendWhy: `أرسل (${ltr('Enter')})`,
    // What everyone says out loud, including in Arabic. An invented word here
    // would be a label for a button nobody would recognise.
    emoji: 'إيموجي',
    count: (length: number, max: number) => `${length}/${max}`,
    save: 'احفظ',
    saveWhy: `احفظ التعديل (${ltr('Enter')})`,
    editing: 'تعدّل رسالتك',
    cancelEdit: 'ألغِ التعديل',
    attach: 'أضف صورة',
    attachRemove: 'أزل الصورة',
    attaching: 'جارٍ التحضير…',
    attached: 'جاهزة للإرسال',
    refused: (reason: ImageRefusal): string =>
      reason === 'not-an-image'
        ? 'هذا ليس صورة يستطيع المتصفح قراءتها.'
        : reason === 'too-many-pixels'
          ? 'هذه الصورة أكبر من أن تُفتح هنا.'
          : reason === 'too-heavy'
            ? 'الصورة ثقيلة حتى بعد تصغيرها. جرّب واحدة أصغر.'
            : reason === 'no-encoder'
              ? 'لا يستطيع هذا المتصفح تحضير صورة للإرسال.'
              : reason === 'off'
                ? 'الصور غير مفعّلة هنا.'
                : reason === 'daily'
                  ? 'أرسلت اليوم أقصى عدد من الصور.'
                  : reason === 'full'
                    ? 'لا مكان لمزيد من الصور الآن.'
                    : 'لم تصل إلى الخادم. حاول مرة أخرى.',
  },

  reply: {
    to: (name: ReactNode) => ['ترد على ', name],
    cancel: 'ألغِ الرد',
    quote: 'اذهب إلى الرسالة التي يرد عليها',
  },

  mention: {
    label: 'من يمكنك الإشارة إليهم',
    you: 'هذه إشارة إليك',
  },

  image: {
    alt: 'صورة لم يصفها أحد',
    open: 'افتح الصورة بحجمها الكامل',
    full: 'الصورة بحجمها الكامل',
    close: 'أغلق الصورة',
    closeWhy: `أغلق الصورة (${ltr('Escape')})`,
    gone: 'لم تعد هذه الصورة موجودة.',
  },

  reactions: {
    label: 'التفاعلات',
    add: 'أضف تفاعلًا',
    count: (n: number) =>
      plural(n, {
        zero: 'لا أحد تفاعل',
        one: 'تفاعل شخص واحد',
        two: 'تفاعل شخصان',
        few: `تفاعل ${n} أشخاص`,
        many: `تفاعل ${n} شخصًا`,
        other: `تفاعل ${n} شخص`,
      }),
    full: 'بلغت هذه الرسالة أقصى عدد من التفاعلات.',
  },

  menu: {
    open: 'ما يمكنك فعله بهذه الرسالة',
    label: 'إجراءات الرسالة',
    react: 'تفاعل',
    reply: 'رد',
    pin: 'ثبّت',
    unpin: 'أزل التثبيت',
    pinWhy: 'الرسالة المثبتة تبقى بعد أن تختفي بقية رسائل الغرفة',
    pinGated: 'التثبيت لمن سجّل الدخول',
    pinFull: 'هذه الغرفة تحمل أقصى عدد من الرسائل المثبتة.',
    copy: 'انسخ الرابط',
    copied: 'نُسخ الرابط.',
    copyManual: `انسخه بـ ${ltr('Ctrl+C')} — لم يسمح هذا المتصفح للصفحة بالوصول إلى الحافظة.`,
    copyLabel: 'رابط هذه الرسالة',
    edit: 'عدّل',
    editWhy: 'تُظهر الغرفة أن الرسالة عُدِّلت، ومتى',
    delete: 'احذف',
    deleteWhy: 'تختفي الكلمات عن الجميع، ولا يمكن استرجاعها',
    report: 'أبلغ',
    block: 'احظر',
    unblock: 'ألغِ الحظر',
    blockWhy: 'يخفي رسائله في هذا المتصفح وحده. لا يُبلَّغ أحد، ولا يُرسل شيء.',
  },

  pins: {
    count: (n: number) =>
      plural(n, {
        zero: 'لا رسائل مثبتة',
        one: 'رسالة واحدة مثبتة',
        two: 'رسالتان مثبتتان',
        few: `${n} رسائل مثبتة`,
        many: `${n} رسالةً مثبتة`,
        other: `${n} رسالة مثبتة`,
      }),
    show: 'أظهر الرسائل المثبتة',
    hide: 'أخفِ الرسائل المثبتة',
    label: 'الرسائل المثبتة',
    open: 'اذهب إلى هذه الرسالة',
  },

  search: {
    open: 'ابحث في هذه الغرفة',
    close: 'أغلق البحث',
    placeholder: 'ابحث',
    go: 'ابحث',
    rule: () => searchRule('ar'),
    busy: 'جارٍ البحث…',
    found: (n: number, q: ReactNode) => [
      plural(n, {
        zero: 'لا نتائج لـ',
        one: 'نتيجة واحدة لـ',
        two: 'نتيجتان لـ',
        few: `${n} نتائج لـ`,
        many: `${n} نتيجةً لـ`,
        other: `${n} نتيجة لـ`,
      }),
      ' «',
      q,
      '»',
    ],
    none: (q: ReactNode) => ['لا شيء يطابق «', q, '».'],
    back: 'عد إلى المحادثة',
    result: 'أظهر هذه الرسالة داخل المحادثة',
  },

  inbox: {
    open: (n: number) => mineAr(n),
    title: 'الإشارات',
    label: 'من ذكر اسمك',
    empty: 'لم يذكر أحد اسمك بعد.',
    loading: 'جارٍ التحميل…',
    from: (name: ReactNode, room: RoomId) => [name, ' في ', roomLabel(room, 'ar')],
    go: 'افتح هذه الرسالة',
    markAll: 'علّم الكل كمقروء',
    notify: 'نبّهني',
    notifyWhy: 'تنبيه من المتصفح حين يذكرك أحد. في هذا المتصفح وحده، وما دام هناك تبويب مفتوح.',
    elsewhere: (n: number) =>
      plural(n, {
        zero: 'لا جديد في غرف أخرى',
        one: 'غرفة أخرى فيها جديد',
        two: 'غرفتان أخريان فيهما جديد',
        few: `${n} غرف أخرى فيها جديد`,
        many: `${n} غرفةً أخرى فيها جديد`,
        other: `${n} غرفة أخرى فيها جديد`,
      }),
    signedOut: 'تُحفظ الإشارات مع حسابك. سجّل الدخول لتتبعك أينما فتحت المحادثة.',
    close: 'أغلق الإشارات',
  },

  report: {
    title: 'أبلغ عن هذه الرسالة',
    quoted: 'الرسالة',
    reason: 'ما المشكلة فيها؟',
    reasonNote: 'اختياري. جملة واحدة تكفي.',
    send: 'أبلغ',
    cancel: 'ألغِ',
    what: 'يصل هذا إلى من يدير هذا الموقع. سيرى الرسالة ووسمك وما تكتبه هنا. ولا يُبلَّغ صاحب الرسالة.',
    sent: 'تم الإبلاغ. لا يحدث شيء آخر هنا.',
    close: 'أغلق',
  },

  remove: {
    title: 'احذف هذه الرسالة',
    quoted: 'الرسالة',
    what: 'تختفي الكلمات عن الجميع، هنا وفي كل رد اقتبسها. ويبقى مكان الرسالة صفًّا فارغًا، حتى يظل ما حولها مفهومًا.',
    undo: 'لا يمكن التراجع عن هذا.',
    confirm: 'احذف',
    cancel: 'ألغِ',
  },

  members: {
    label: 'في هذه الغرفة',
    empty: 'لا أحد هنا بعد.',
  },

  who: {
    open: 'من هذا',
    title: 'من هذا',
    back: 'عد إلى المحادثة',
    here: 'موجود في هذه الغرفة الآن',
    gone: 'ليس في هذه الغرفة الآن',
    goneNote: 'هذا ما كان يظهر عنه آخر مرة كتب فيها هنا.',
    objective: (n: number) => `يعمل على الهدف ${n}`,
    you: 'هذا أنت.',
    mention: 'اذكره',
    mentionWhy: 'يضع اسمه في مربع الكتابة، فتصله الإشارة وتبقى في انتظاره إن أغلق الصفحة.',
    mentionGone: 'لا يمكن ذكره إلا وهو في الغرفة.',
    what: 'ما قيل للغرفة. الاسم اسم الحساب، أما المستوى والرتبة فهما ما قاله متصفحه.',

    record: 'سجلّه',
    loading: 'جارٍ التحميل…',
    cleared: (n: number) =>
      plural(n, {
        zero: 'لم يُنجز أي مستوى',
        one: 'أنجز مستوى واحدًا',
        two: 'أنجز مستويين',
        few: `أنجز ${n} مستويات`,
        many: `أنجز ${n} مستوىً`,
        other: `أنجز ${n} مستوى`,
      }),
    stars: (n: number) =>
      plural(n, {
        zero: 'بلا نجوم',
        one: 'نجمة واحدة',
        two: 'نجمتان',
        few: `${n} نجوم`,
        many: `${n} نجمةً`,
        other: `${n} نجمة`,
      }),
    joined: (date: string) => `انضم في ${date}`,
    seen: (date: string) => `آخر ظهور ${date}`,
    noRecord: 'لا شيء محفوظ على هذا الحساب بعد.',
    noAccount: 'هذا الاسم اسم متصفح لا اسم حساب، فلا سجلّ له.',
    claim: 'محسوبة مما حفظه متصفحه. لا شيء منها مُتحقَّق منه.',
    signedOut: 'سجّل الدخول لترى ما أنجزه الآخرون.',
    page: 'السجلّ المؤكَّد',
    pageWhy: 'يفتح صفحته العامة في تبويب جديد، وفيها الإنجازات التي فحصها خادم فقط.',
  },

  teams: {
    open: (n: number) =>
      n > 0
        ? `الفرق، ${plural(n, {
            zero: 'لا دعوات',
            one: 'دعوة واحدة',
            two: 'دعوتان',
            few: `${n} دعوات`,
            many: `${n} دعوةً`,
            other: `${n} دعوة`,
          })}`
        : 'الفرق',
    title: 'الفرق',
    asked: 'دعوات للانضمام',
    mine: 'فرقك',
    none: 'لست في أي فريق بعد.',
    loading: 'جارٍ التحميل…',
    size: (n: number) =>
      plural(n, {
        zero: 'لا أحد',
        one: 'شخص واحد',
        two: 'شخصان',
        few: `${n} أشخاص`,
        many: `${n} شخصًا`,
        other: `${n} شخص`,
      }),
    join: 'انضم',
    decline: 'ارفض',
    lead: 'قائد',
    youLead: 'أنت قائد هذا الفريق.',
    leave: 'غادر',
    new: 'فريق جديد',
    make: 'أنشئ',
    cancel: 'ألغِ',
    nameLabel: 'سمِّ الفريق',
    invite: 'ادعه إلى فريق',
    inviteWhy: 'ترسل إليه دعوة. القرار له، ولا يحدث شيء قبل أن يقبل.',
    inviteTo: 'إلى أي فريق تدعوه؟',
    invited: 'أُرسلت الدعوة. سيراها عند حضوره القادم.',
    inviteNoTeam: 'أنشئ فريقًا أولًا ثم ادعُ الناس إليه.',
    inviteNoAccount: 'لا يُدعى إلى فريق إلا صاحب حساب، وهذا الاسم اسم متصفح.',
    signedOut: 'الفرق محفوظة مع حسابك. سجّل الدخول لتنشئ فريقًا.',

    tooMany: (() => `يمكنك أن تكون في ${LIMITS.team.perLearner} فرق في وقت واحد.`)(),
    teamFull: (() => `يتسع الفريق لـ ${LIMITS.team.size} أشخاص.`)(),
    alreadyIn: 'هو في الفريق بالفعل.',
    alreadyAsked: 'سبقت دعوته.',
    notLead: 'الدعوة لقائد الفريق وحده.',
    badName: (() => `اسم الفريق من ${LIMITS.team.name.min} إلى ${LIMITS.team.name.max} حرفًا.`)(),
    failed: 'لم ينجح ذلك. حاول بعد قليل.',

    privacy: 'لا يقرأ هذه الغرفة إلا فريقك، ومعه من يدير EngineerOS — وبذلك يُعالَج ما يُبلَّغ عنه هنا.',
  },

  support: {
    title: 'الدعم',
    open: (reply: boolean): string => (reply ? 'الدعم، وصلك رد' : 'الدعم'),
    signedOut: 'محادثة الدعم محفوظة مع حسابك. سجّل الدخول لتبدأ واحدة.',
    closed: 'لا أحد يستقبل الأسئلة هنا في الوقت الحالي.',
    loading: 'جارٍ التحميل…',
    here: (n: number) =>
      plural(n, {
        zero: 'لا أحد يرد الآن. اترك سؤالك على أي حال، فسيُقرأ.',
        one: 'شخص واحد يرد على الأسئلة الآن.',
        two: 'شخصان يردان على الأسئلة الآن.',
        few: `${n} أشخاص يردون على الأسئلة الآن.`,
        many: `${n} شخصًا يردون على الأسئلة الآن.`,
        other: `${n} شخص يرد على الأسئلة الآن.`,
      }),

    about: 'عمّ تسأل؟',
    topics: {
      access: 'الدخول',
      billing: 'الدفع',
      tasks: 'المهامّ المدفوعة ورصيد الذكاء الاصطناعي',
      content: 'خطأ في مستوى',
      bug: 'شيء لا يعمل',
      other: 'شيء آخر',
    },
    subjectLabel: 'سطر واحد عن الموضوع، إن أردت',
    start: 'ابدأ المحادثة',

    yours: 'محادثتك',
    stateOpen: 'مفتوحة',
    stateResolved: 'مغلقة',
    waiting: 'لم يرد أحد بعد.',
    replied: 'هناك رد بانتظارك فيها.',
    withAgent: (name: ReactNode) => [name, ' يتابع هذه المحادثة.'],
    unclaimed: 'لم يستلمها أحد بعد.',
    opened: (date: string) => `فُتحت في ${date}`,
    openRoom: 'افتح المحادثة',
    openRoomWhy: 'تفتحها في اللوحة وتبقى فيها وأنت تتنقل بين المستويات.',
    resolveIt: 'انتهيت',
    resolveWhy: 'يغلقها. وإن كتبت فيها مرة أخرى فُتحت من جديد.',
    past: 'محادثات مغلقة',

    busy: (() => `يمكنك بدء ${SUPPORT_LIMITS.perDay} محادثات في اليوم. عاود المحاولة غدًا.`)(),
    failed: 'لم ينجح ذلك. حاول بعد قليل.',

    privacy: 'يقرأ من يدير EngineerOS كل ما في هذه المحادثة، فهو من يرد عليها. ولا يراها أحد من المتعلمين غيرك.',
  },

  rooms: {
    label: (room: RoomId) => roomLabel(room, 'ar'),
    change: 'غيّر الغرفة',
    follows: 'تلقائي',
    followsWhy: 'تتغير الغرفة تلقائيًا بحسب المستوى المفتوح',
    number: 'غرفة',
    range: (min: number, max: number) => `${min}–${max}`,
    go: 'اذهب',
    thisLevel: 'هذا المستوى',
    thisLevelWhy: 'انتقل تلقائيًا إلى غرفة المستوى المفتوح',
    recent: 'الأخيرة',
  },

  name: {
    pick: 'اختر اسمًا',
    change: 'غيّر الاسم',
    join: 'انضم',
    joinRoom: (room: RoomId) => `انضم إلى ${roomLabel(room, 'ar')}`,
    save: 'احفظ',
    cancel: 'ألغِ',
    inlineNote: 'اختر اسمًا لتشارك في المحادثة. يمكنك القراءة فقط حتى ذلك الحين.',
    note: 'سيظهر الاسم بجانب رسائلك مع وسم خاص بهذا المتصفح. لا تحتاج إلى حساب.',
  },

  signIn: {
    title: 'سجّل الدخول لتكتب',
    note: (room: RoomId) =>
      `أنت في ${roomLabel(room, 'ar')}. سجّل الدخول ليظهر اسمك ووسمك نفسيهما في كل غرفة وعلى جميع أجهزتك.`,
    button: 'سجّل الدخول',
    busy: 'جارٍ تسجيل الدخول…',
    why: 'شارك باسم حسابك نفسه هنا وفي أي متصفح آخر',
    verified: 'مسجَّل الدخول بحساب',
    verifiedShort: 'مسجَّل الدخول',
  },

  faces: {
    label: 'وجوه',
    byDefault: 'صورتك الافتراضية',
    useDefault: (face: string) => `استخدم الصورة المرتبطة بوسمك (${face})`,
  },

  emoji: {
    search: 'ابحث',
    searchLabel: 'ابحث في الإيموجي',
    groups: 'المجموعات',
    results: 'النتائج',
    recent: 'الأخيرة',
    none: 'لا شيء يطابق ذلك.',
    group: (g: EmojiGroup) => groupName(g, 'ar'),
    name: (e: CustomEmoji) => customName(e, 'ar'),
    cell: (e: CustomEmoji) => `${customName(e, 'ar')} — ${ltr(e.c)}`,
  },

  dock: {
    toggle: (open: boolean, unread: number, mentions: number) => toggleAr(open, unread, mentions),
    toggleWhy: (open: boolean, unread: number, mentions: number) => `${toggleAr(open, unread, mentions)} (${CHAT_SHORTCUT})`,
    panel: 'المحادثة',
    width: 'عرض المحادثة',
    height: 'ارتفاع المحادثة',
    resize: 'اسحب لتغيير الحجم',
    resizeReset: `اسحب لتغيير الحجم · ${ltr('Home')} للإعادة`,
  },

  rules: {
    name: () => nameRule('ar'),
    text: () => textRule('ar'),
    notYours: 'لا يمكنك تغيير إلا رسالة أرسلتها من هذا المتصفح.',
  },
};

/* ── German ───────────────────────────────────────────────────────────────── */

function hereDe(n: number): string {
  return `${n} hier`;
}

function mineDe(n: number): string {
  return `${n} für dich`;
}

function toggleDe(open: boolean, unread: number, mentions: number): string {
  if (open) return 'Chat schließen';
  const news: string[] = [];
  if (unread > 0) news.push(`${unread} ungelesen`);
  if (mentions > 0) news.push(mineDe(mentions));
  return news.length > 0 ? `Chat öffnen, ${news.join(', ')}` : 'Chat öffnen';
}

export const de: typeof en = {
  panel: {
    whoIsHere: 'Wer in diesem Raum ist',
    here: (n) => hereDe(n),
    hereUnknown: '—',
    close: 'Chat schließen',
    closeWhy: `Chat schließen (${ltr('Escape')})`,
    changeFace: 'Gesicht ändern',
    accountPicture: 'Dein Kontobild',
    changeName: 'Namen ändern',
  },
  strip: {
    label: 'Offene Räume',
    tab: (room, unread, mentions = 0) => {
      if (unread <= 0) return roomLabel(room, 'de');
      const waiting = `${roomLabel(room, 'de')}, ${unread} ungelesen`;
      return mentions > 0 ? `${waiting}, ${mineDe(mentions)}` : waiting;
    },
    why: 'Der Raum, in dem du warst, und der Raum zu dem Level, in dem du gerade bist',
  },
  occupancy: {
    here: (n) => hereDe(n),
    quiet: (days) => (days < 1 ? 'leer, heute ruhig' : `leer, seit ${days} ${days === 1 ? 'Tag' : 'Tagen'} ruhig`),
    never: 'leer',
  },
  status: {
    listening: 'hört zu',
    connecting: 'verbinde',
    roomFull: 'Raum voll – neuer Versuch',
    offline: 'offline – neuer Versuch',
    banned: (reason) => (reason ? `gesperrt – ${reason}` : 'gesperrt'),
  },
  list: {
    label: 'Nachrichten',
    empty: 'Hier ist noch nichts.',
    earlier: 'Frühere Nachrichten',
    loadingEarlier: 'Wird geladen …',
    notSent: 'nicht gesendet',
    retry: 'Erneut versuchen',
    today: 'Heute',
    yesterday: 'Gestern',
    removed: 'Von den Betreibern dieser Installation entfernt.',
    deleted: 'Von der Person gelöscht, die es geschrieben hat.',
    edited: 'bearbeitet',
    editedAt: (when) => `Bearbeitet um ${when}`,
    blocked: 'Blockiert',
    show: 'Zeigen',
    hide: 'Ausblenden',
    pinned: 'Angeheftet',
    cleared: (level) => clearedLine(level, 'de'),
  },
  composer: {
    message: 'Nachricht',
    send: 'Senden',
    sendWhy: `Senden (${ltr('Enter')})`,
    emoji: 'Emoji',
    count: (length, max) => `${length}/${max}`,
    save: 'Speichern',
    saveWhy: `Die Änderung speichern (${ltr('Enter')})`,
    editing: 'Du bearbeitest deine Nachricht',
    cancelEdit: 'Bearbeiten beenden',
    attach: 'Bild hinzufügen',
    attachRemove: 'Bild entfernen',
    attaching: 'Wird vorbereitet …',
    attached: 'Bereit zum Senden',
    refused: (reason) =>
      reason === 'not-an-image'
        ? 'Das ist kein Bild, das dieser Browser lesen kann.'
        : reason === 'too-many-pixels'
          ? 'Dieses Bild ist zu groß, um es hier zu öffnen.'
          : reason === 'too-heavy'
            ? 'Dieses Bild ist selbst verkleinert zu schwer. Versuch ein kleineres.'
            : reason === 'no-encoder'
              ? 'Dieser Browser kann kein Bild zum Senden vorbereiten.'
              : reason === 'off'
                ? 'Bilder sind hier nicht eingeschaltet.'
                : reason === 'daily'
                  ? 'Du hast heute so viele Bilder geschickt, wie du kannst.'
                  : reason === 'full'
                    ? 'Gerade ist kein Platz für weitere Bilder.'
                    : 'Das hat den Server nicht erreicht. Versuch es noch einmal.',
  },
  reply: {
    to: (name) => ['Antwort an ', name],
    cancel: 'Antwort abbrechen',
    quote: 'Zu der Nachricht springen, auf die das antwortet',
  },
  mention: {
    label: 'Leute, die du erwähnen kannst',
    you: 'Das bist du',
  },
  image: {
    alt: 'Ein Bild, das niemand beschrieben hat',
    open: 'Das Bild in voller Größe öffnen',
    full: 'Das Bild in voller Größe',
    close: 'Bild schließen',
    closeWhy: `Bild schließen (${ltr('Escape')})`,
    gone: 'Dieses Bild ist nicht mehr da.',
  },
  reactions: {
    label: 'Reaktionen',
    add: 'Reaktion hinzufügen',
    count: (n) => `${n} haben reagiert`,
    full: 'Diese Nachricht hat so viele Reaktionen, wie sie fassen kann.',
  },
  menu: {
    open: 'Was du mit dieser Nachricht tun kannst',
    label: 'Aktionen für die Nachricht',
    react: 'Reagieren',
    reply: 'Antworten',
    pin: 'Anheften',
    unpin: 'Lösen',
    pinWhy: 'Eine angeheftete Nachricht bleibt, wenn der Rest des Raums wegscrollt',
    pinGated: 'Anheften können nur angemeldete Leute',
    pinFull: 'Dieser Raum hat so viele angeheftete Nachrichten, wie er fassen kann.',
    copy: 'Link kopieren',
    copied: 'Link kopiert.',
    copyManual: `Kopier ihn mit ${ltr('Strg+C')} – dieser Browser lässt die Seite nicht an die Zwischenablage.`,
    copyLabel: 'Link zu dieser Nachricht',
    edit: 'Bearbeiten',
    editWhy: 'Der Raum zeigt, dass eine Nachricht bearbeitet wurde, und wann',
    delete: 'Löschen',
    deleteWhy: 'Die Worte verschwinden für alle und lassen sich nicht zurückholen',
    report: 'Melden',
    block: 'Blockieren',
    unblock: 'Blockierung aufheben',
    blockWhy: 'Blendet die Nachrichten der Person in diesem Browser aus. Niemand wird benachrichtigt, und nichts wird gesendet.',
  },
  pins: {
    count: (n) => `${n} angeheftet`,
    show: 'Angeheftete Nachrichten zeigen',
    hide: 'Angeheftete Nachrichten ausblenden',
    label: 'Angeheftete Nachrichten',
    open: 'Zu dieser Nachricht springen',
  },
  search: {
    open: 'Diesen Raum durchsuchen',
    close: 'Suche schließen',
    placeholder: 'Suchen',
    go: 'Suchen',
    rule: () => searchRule('de'),
    busy: 'Suche läuft …',
    found: (n, q) => [`${n} Treffer für „`, q, '“'],
    none: (q) => ['Nichts passt zu „', q, '“.'],
    back: 'Zurück zum Gespräch',
    result: 'Im Gespräch zeigen',
  },
  inbox: {
    open: (n) => mineDe(n),
    title: 'Erwähnungen',
    label: 'Leute, die deinen Namen gesagt haben',
    empty: 'Noch hat niemand deinen Namen gesagt.',
    loading: 'Wird geladen …',
    from: (name, room) => [name, ' in ', roomLabel(room, 'de')],
    go: 'Diese Nachricht öffnen',
    markAll: 'Alle als gesehen markieren',
    notify: 'Benachrichtige mich',
    notifyWhy: 'Eine Browser-Benachrichtigung, wenn dich jemand nennt. Nur in diesem Browser und nur, solange ein Tab offen ist.',
    elsewhere: (n) => (n === 1 ? 'In 1 anderen Raum gibt es Neues' : `In ${n} anderen Räumen gibt es Neues`),
    signedOut: 'Erwähnungen werden mit deinem Konto gespeichert. Melde dich an, dann folgen sie dir.',
    close: 'Erwähnungen schließen',
  },
  report: {
    title: 'Diese Nachricht melden',
    quoted: 'Die Nachricht',
    reason: 'Was ist daran falsch?',
    reasonNote: 'Optional. Ein Satz reicht.',
    send: 'Melden',
    cancel: 'Abbrechen',
    what: 'Das geht an die Betreiber dieser Installation. Sie sehen die Nachricht, deinen Tag und was du hier schreibst. Die gemeldete Person erfährt nichts davon.',
    sent: 'Gemeldet. Hier passiert sonst nichts weiter.',
    close: 'Schließen',
  },
  remove: {
    title: 'Diese Nachricht löschen',
    quoted: 'Die Nachricht',
    what: 'Die Worte verschwinden für alle, hier und in jeder Antwort, die sie zitiert hat. An der Stelle der Nachricht bleibt eine leere Zeile, damit das Gespräch drumherum noch Sinn ergibt.',
    undo: 'Das lässt sich nicht rückgängig machen.',
    confirm: 'Löschen',
    cancel: 'Abbrechen',
  },
  members: {
    label: 'In diesem Raum',
    empty: 'Noch niemand hier.',
  },
  who: {
    open: 'Wer das ist',
    title: 'Wer das ist',
    back: 'Zurück zum Gespräch',
    here: 'Gerade in diesem Raum',
    gone: 'Gerade nicht in diesem Raum',
    goneNote: 'Das hat die Person gezeigt, als sie zuletzt hier geschrieben hat.',
    objective: (n) => `Arbeitet an Ziel ${n}`,
    you: 'Das bist du.',
    mention: 'Erwähnen',
    mentionWhy: 'Setzt den Namen ins Feld. Er landet in den Erwähnungen der Person und wartet dort, wenn sie den Tab schließt.',
    mentionGone: 'Um erwähnt zu werden, muss die Person im Raum sein.',
    what: 'Was dem Raum gesagt wurde. Der Name gehört zum Konto; Level und Rang hat ihr Browser gemeldet.',
    record: 'Ihr Stand',
    loading: 'Wird geladen …',
    cleared: (n) => `${n} Level geschafft`,
    stars: (n) => (n === 1 ? '1 Stern' : `${n} Sterne`),
    joined: (date) => `Dabei seit ${date}`,
    seen: (date) => `Zuletzt hier ${date}`,
    noRecord: 'Zu diesem Konto wurde noch nichts synchronisiert.',
    noAccount: 'Dieser Name gehört einem Browser, nicht einem Konto – es gibt keinen Stand, der gespeichert wird.',
    claim: 'Gezählt aus dem, was ihr Browser gespeichert hat. Nichts davon wird geprüft.',
    signedOut: 'Melde dich an, um zu sehen, was jemand geschafft hat.',
    page: 'Bestätigter Nachweis',
    pageWhy: 'Öffnet die öffentliche Seite in einem neuen Tab. Sie zeigt nur die Abschlüsse, die ein Server geprüft hat.',
  },
  teams: {
    open: (n) => (n > 0 ? (n === 1 ? 'Teams, 1 Einladung' : `Teams, ${n} Einladungen`) : 'Teams'),
    title: 'Teams',
    asked: 'Eingeladen',
    mine: 'Deine Teams',
    none: 'Du bist noch in keinem Team.',
    loading: 'Wird geladen …',
    size: (n) => (n === 1 ? '1 Person' : `${n} Personen`),
    join: 'Beitreten',
    decline: 'Ablehnen',
    lead: 'Leitung',
    youLead: 'Du leitest dieses Team.',
    leave: 'Verlassen',
    new: 'Neues Team',
    make: 'Gründen',
    cancel: 'Abbrechen',
    nameLabel: 'Gib ihm einen Namen',
    invite: 'In ein Team einladen',
    inviteWhy: 'Fragt die Person, ob sie beitreten will. Sie entscheidet, und bis dahin passiert nichts.',
    inviteTo: 'In welches Team einladen?',
    invited: 'Gefragt. Die Person sieht es, wenn sie das nächste Mal hier ist.',
    inviteNoTeam: 'Gründe zuerst ein Team, dann lade Leute ein.',
    inviteNoAccount: 'Nur ein Konto kann in ein Team eingeladen werden – dieser Name gehört einem Browser.',
    signedOut: 'Teams werden mit deinem Konto gespeichert. Melde dich an, um eins zu gründen.',
    tooMany: (() => `Du kannst in ${LIMITS.team.perLearner} Teams gleichzeitig sein.`)(),
    teamFull: (() => `Ein Team fasst ${LIMITS.team.size} Personen.`)(),
    alreadyIn: 'Die Person ist schon drin.',
    alreadyAsked: 'Die Person wurde schon gefragt.',
    notLead: 'Nur die Leitung eines Teams kann Leute einladen.',
    badName: (() => `Ein Teamname hat ${LIMITS.team.name.min} bis ${LIMITS.team.name.max} Zeichen.`)(),
    failed: 'Das hat nicht geklappt. Versuch es gleich noch einmal.',
    privacy: 'Nur dein Team kann diesen Raum lesen. Die Betreiber von EngineerOS auch – so wird mit allem umgegangen, was hier gemeldet wird.',
  },
  support: {
    title: 'Support',
    open: (reply) => (reply ? 'Support, eine Antwort wartet' : 'Support'),
    signedOut: 'Ein Gespräch mit dem Support wird mit deinem Konto gespeichert. Melde dich an, um eins zu beginnen.',
    closed: 'Hier nimmt gerade niemand Fragen an.',
    loading: 'Wird geladen …',
    here: (n) =>
      n === 0
        ? 'Gerade antwortet niemand. Stell die Frage trotzdem – sie wird gelesen.'
        : n === 1
          ? 'Gerade beantwortet 1 Person Fragen.'
          : `Gerade beantworten ${n} Personen Fragen.`,
    about: 'Worum geht es?',
    topics: {
      access: 'Reinkommen',
      billing: 'Zahlung',
      tasks: 'Bezahlte Aufgaben und KI-Guthaben',
      content: 'Ein Level ist falsch',
      bug: 'Etwas ist kaputt',
      other: 'Etwas anderes',
    },
    subjectLabel: 'Eine Zeile dazu, wenn du magst',
    start: 'Gespräch beginnen',
    yours: 'Dein Gespräch',
    stateOpen: 'offen',
    stateResolved: 'geschlossen',
    waiting: 'Noch hat niemand geantwortet.',
    replied: 'Darin wartet eine Antwort.',
    withAgent: (name) => [name, ' kümmert sich darum.'],
    unclaimed: 'Noch hat es niemand übernommen.',
    opened: (date) => `Geöffnet ${date}`,
    openRoom: 'Gespräch öffnen',
    openRoomWhy: 'Öffnet es im Panel und behält es dort, während du dich durch den Kurs bewegst.',
    resolveIt: 'Ich bin fertig',
    resolveWhy: 'Schließt es. Wenn du wieder darin schreibst, öffnet es sich erneut.',
    past: 'Geschlossene Gespräche',
    busy: (() => `Du kannst ${SUPPORT_LIMITS.perDay} Gespräche am Tag beginnen. Versuch es morgen wieder.`)(),
    failed: 'Das hat nicht geklappt. Versuch es gleich noch einmal.',
    privacy: 'Die Betreiber von EngineerOS lesen alles in diesem Gespräch – sie sind es, die antworten. Keine andere lernende Person kann es sehen.',
  },
  rooms: {
    label: (room) => roomLabel(room, 'de'),
    change: 'Raum wechseln',
    follows: 'folgt',
    followsWhy: 'Dieser Raum folgt dem Level, in dem du bist',
    number: 'Raum',
    range: (min, max) => `${min}–${max}`,
    go: 'Los',
    thisLevel: 'Dieses Level',
    thisLevelWhy: 'Dem Level folgen, in dem du bist',
    recent: 'Zuletzt',
  },
  name: {
    pick: 'Wähl einen Namen',
    change: 'Namen ändern',
    join: 'Beitreten',
    joinRoom: (room) => `${roomLabel(room, 'de')} beitreten`,
    save: 'Speichern',
    cancel: 'Abbrechen',
    inlineNote: 'Wähl einen Namen, um mitzumachen. Bis dahin liest du nur mit.',
    note: 'Wird neben dem angezeigt, was du schreibst, mit einem Tag, den nur dieser Browser hat. Kein Konto.',
  },
  signIn: {
    title: 'Zum Posten anmelden',
    note: (room) =>
      `Du liest ${roomLabel(room, 'de')}. Mit der Anmeldung hast du einen Namen und einen Tag in jedem Raum, auf jedem Gerät.`,
    button: 'Anmelden',
    busy: 'Anmeldung läuft …',
    why: 'Mit deinem Konto posten, hier und in jedem anderen Browser',
    verified: 'Mit einem Konto angemeldet',
    verifiedShort: 'angemeldet',
  },
  faces: {
    label: 'Gesichter',
    byDefault: 'Deins als Voreinstellung',
    useDefault: (face) => `Das nehmen, das mein Tag mir gibt (${face})`,
  },
  emoji: {
    search: 'Suchen',
    searchLabel: 'Emoji suchen',
    groups: 'Gruppen',
    results: 'Ergebnisse',
    recent: 'Zuletzt',
    none: 'Nichts passt dazu.',
    group: (g) => groupName(g, 'de'),
    name: (e) => customName(e, 'de'),
    cell: (e) => `${customName(e, 'de')} – ${ltr(e.c)}`,
  },
  dock: {
    toggle: (open, unread, mentions) => toggleDe(open, unread, mentions),
    toggleWhy: (open, unread, mentions) => `${toggleDe(open, unread, mentions)} (${CHAT_SHORTCUT})`,
    panel: 'Chat',
    width: 'Chatbreite',
    height: 'Chathöhe',
    resize: 'Ziehen, um die Größe zu ändern',
    resizeReset: `Ziehen, um die Größe zu ändern · ${ltr('Pos1')} zum Zurücksetzen`,
  },
  rules: {
    name: () => nameRule('de'),
    text: () => textRule('de'),
    notYours: 'Du kannst nur eine Nachricht ändern, die du aus diesem Browser gesendet hast.',
  },
};

function jaHere(n: number): string {
  return `${n}人`;
}

function jaMine(n: number): string {
  return `あなた宛て${n}件`;
}

function jaToggle(open: boolean, unread: number, mentions: number): string {
  if (open) return 'チャットを閉じる';
  const news: string[] = [];
  if (unread > 0) news.push(`未読${unread}件`);
  if (mentions > 0) news.push(jaMine(mentions));
  return news.length > 0 ? `チャットを開く、${news.join('、')}` : 'チャットを開く';
}

export const ja: typeof en = {
  panel: {
    whoIsHere: 'このルームにいる人',
    here: (n) => jaHere(n),
    hereUnknown: '—',
    close: 'チャットを閉じる',
    closeWhy: `チャットを閉じる(${ltr('Escape')})`,
    changeFace: '顔を変更',
    accountPicture: 'アカウントの画像',
    changeName: '名前を変更',
  },
  strip: {
    label: '開いているルーム',
    tab: (room, unread, mentions = 0) => {
      if (unread <= 0) return roomLabel(room, 'ja');
      const waiting = `${roomLabel(room, 'ja')}、未読${unread}件`;
      return mentions > 0 ? `${waiting}、${jaMine(mentions)}` : waiting;
    },
    why: '前にいたルームと、今いるレベルのルーム',
  },
  occupancy: {
    here: (n) => jaHere(n),
    quiet: (days) => (days < 1 ? '誰もいません。今日は静かです' : `誰もいません。${days}日間静かです`),
    never: '誰もいません',
  },
  status: {
    listening: '受信中',
    connecting: '接続中',
    roomFull: 'ルームが満員です。再試行中',
    offline: 'オフライン。再試行中',
    banned: (reason) => (reason ? `利用停止中 — ${reason}` : '利用停止中'),
  },
  list: {
    label: 'メッセージ',
    empty: 'まだ何もありません。',
    earlier: '以前のメッセージ',
    loadingEarlier: '読み込み中…',
    notSent: '未送信',
    retry: '再試行',
    today: '今日',
    yesterday: '昨日',
    removed: 'このデプロイの運営者によって削除されました。',
    deleted: '書いた本人によって削除されました。',
    edited: '編集済み',
    editedAt: (when) => `${when}に編集`,
    blocked: 'ブロック中',
    show: '表示',
    hide: '非表示',
    pinned: 'ピン留め',
    cleared: (level) => clearedLine(level, 'ja'),
  },
  composer: {
    message: 'メッセージ',
    send: '送信',
    sendWhy: `送信(${ltr('Enter')})`,
    emoji: '絵文字',
    count: (length, max) => `${length}/${max}`,
    save: '保存',
    saveWhy: `変更を保存(${ltr('Enter')})`,
    editing: 'メッセージを編集中',
    cancelEdit: '編集をやめる',
    attach: '画像を追加',
    attachRemove: '画像を外す',
    attaching: '準備中…',
    attached: '送信の準備完了',
    refused: (reason) =>
      reason === 'not-an-image'
        ? 'このブラウザーで読み込める画像ではありません。'
        : reason === 'too-many-pixels'
          ? '画像が大きすぎて、ここでは開けません。'
          : reason === 'too-heavy'
            ? '縮小しても画像が重すぎます。もっと小さいものを試してください。'
            : reason === 'no-encoder'
              ? 'このブラウザーでは、送信用に画像を準備できません。'
              : reason === 'off'
                ? 'ここでは、画像は有効になっていません。'
                : reason === 'daily'
                  ? '今日送信できる画像の上限に達しました。'
                  : reason === 'full'
                    ? '今は、これ以上画像を受け付ける余裕がありません。'
                    : 'サーバーに届きませんでした。もう一度試してください。',
  },
  reply: {
    to: (name) => ['返信先: ', name],
    cancel: '返信をやめる',
    quote: '返信先のメッセージへ移動',
  },
  mention: {
    label: 'メンションできる人',
    you: 'これはあなたです',
  },
  image: {
    alt: '説明のない画像',
    open: '画像を原寸で開く',
    full: '画像(原寸)',
    close: '画像を閉じる',
    closeWhy: `画像を閉じる(${ltr('Escape')})`,
    gone: 'この画像はもうありません。',
  },
  reactions: {
    label: 'リアクション',
    add: 'リアクションを追加',
    count: (n) => `${n}人がリアクション`,
    full: 'このメッセージには、付けられるリアクションの上限まで付いています。',
  },
  menu: {
    open: 'このメッセージでできること',
    label: 'メッセージの操作',
    react: 'リアクション',
    reply: '返信',
    pin: 'ピン留め',
    unpin: 'ピン留めを外す',
    pinWhy: 'ピン留めしたメッセージは、ルームの他の部分がスクロールで流れても残ります',
    pinGated: 'ピン留めできるのは、ログインしている人だけです',
    pinFull: 'このルームには、ピン留めできるメッセージの上限まで留められています。',
    copy: 'リンクをコピー',
    copied: 'リンクをコピーしました。',
    copyManual: `${ltr('Ctrl+C')}でコピーしてください。このブラウザーでは、ページからクリップボードにアクセスできません。`,
    copyLabel: 'このメッセージへのリンク',
    edit: '編集',
    editWhy: 'メッセージが編集されたことと、その時刻がルームに表示されます',
    delete: '削除',
    deleteWhy: '内容はみんなから消え、元に戻せません',
    report: '報告',
    block: 'ブロック',
    unblock: 'ブロックを解除',
    blockWhy: 'このブラウザーで、相手のメッセージを非表示にします。誰にも通知されず、何も送信されません。',
  },
  pins: {
    count: (n) => `${n}件ピン留め`,
    show: 'ピン留めしたメッセージを表示',
    hide: 'ピン留めしたメッセージを非表示',
    label: 'ピン留めしたメッセージ',
    open: 'このメッセージへ移動',
  },
  search: {
    open: 'このルームを検索',
    close: '検索を閉じる',
    placeholder: '検索',
    go: '検索',
    rule: () => searchRule('ja'),
    busy: '検索中…',
    found: (n, q) => ['「', q, `」が${n}件見つかりました`],
    none: (q) => ['「', q, '」に一致するものはありません。'],
    back: '会話に戻る',
    result: '会話の中で表示',
  },
  inbox: {
    open: (n) => jaMine(n),
    title: 'メンション',
    label: 'あなたの名前を呼んだ人',
    empty: 'まだ誰もあなたの名前を呼んでいません。',
    loading: '読み込み中…',
    from: (name, room) => [name, ' · ', roomLabel(room, 'ja')],
    go: 'このメッセージを開く',
    markAll: 'すべて既読にする',
    notify: '通知する',
    notifyWhy: '誰かに名前を呼ばれたとき、ブラウザーの通知を出します。このブラウザーだけで、タブが開いている間だけ有効です。',
    elsewhere: (n) => `他の${n}つのルームに新着があります`,
    signedOut: 'メンションはアカウントに保存されます。ログインすると、引き継がれます。',
    close: 'メンションを閉じる',
  },
  report: {
    title: 'このメッセージを報告',
    quoted: 'メッセージ',
    reason: 'どこが問題ですか？',
    reasonNote: '任意です。一文で十分です。',
    send: '報告',
    cancel: 'キャンセル',
    what: '報告は、このデプロイの運営者に届きます。運営者には、メッセージ、あなたのタグ、ここに書いた内容が見えます。報告された人には通知されません。',
    sent: '報告しました。ここでは、これ以上何も起きません。',
    close: '閉じる',
  },
  remove: {
    title: 'このメッセージを削除',
    quoted: 'メッセージ',
    what: '内容は、ここでも、引用した返信の中でも、みんなから消えます。メッセージがあった場所には空白の行が残るので、前後の会話の流れは追えます。',
    undo: 'この操作は元に戻せません。',
    confirm: '削除',
    cancel: 'キャンセル',
  },
  members: {
    label: 'このルームにいる人',
    empty: 'まだ誰もいません。',
  },
  who: {
    open: 'この人について',
    title: 'この人について',
    back: '会話に戻る',
    here: '今このルームにいます',
    gone: '今はこのルームにいません',
    goneNote: 'ここに最後に書き込んだときに表示していた内容です。',
    objective: (n) => `目標${n}に取り組み中`,
    you: 'これはあなたです。',
    mention: 'メンションする',
    mentionWhy: '名前を入力欄に入れます。相手のメンションに届き、タブを閉じていても、そこで待っています。',
    mentionGone: 'メンションするには、相手がルームにいる必要があります。',
    what: 'ルームに伝えられた内容です。名前はアカウントのもので、レベルとランクは相手のブラウザーが申告したものです。',
    record: '記録',
    loading: '読み込み中…',
    cleared: (n) => `${n}レベルをクリア`,
    stars: (n) => `星${n}個`,
    joined: (date) => `${date}に参加`,
    seen: (date) => `最終アクセス: ${date}`,
    noRecord: 'このアカウントには、まだ同期されたものがありません。',
    noAccount: 'この名前はブラウザーのもので、アカウントのものではありません。保存される記録はありません。',
    claim: '相手のブラウザーが保存した内容から集計しています。ここでは何も確認されていません。',
    signedOut: '誰かが何をしたか見るには、ログインしてください。',
    page: '検証済みの記録',
    pageWhy: '公開ページを新しいタブで開きます。表示されるのは、サーバーが確認したクリアだけです。',
  },
  teams: {
    open: (n) => (n > 0 ? `チーム、招待${n}件` : 'チーム'),
    title: 'チーム',
    asked: '招待されています',
    mine: 'あなたのチーム',
    none: 'まだどのチームにも入っていません。',
    loading: '読み込み中…',
    size: (n) => `${n}人`,
    join: '参加',
    decline: '辞退',
    lead: 'リード',
    youLead: 'あなたがこのチームのリードです。',
    leave: '退出',
    new: '新しいチーム',
    make: '作成',
    cancel: 'キャンセル',
    nameLabel: 'チーム名',
    invite: 'チームに招待',
    inviteWhy: '参加を依頼します。決めるのは相手で、相手が決めるまで何も起きません。',
    inviteTo: 'どのチームに招待しますか？',
    invited: '依頼しました。相手が次にここに来たときに表示されます。',
    inviteNoTeam: '先にチームを作ってから、招待してください。',
    inviteNoAccount: 'チームに招待できるのは、アカウントだけです。この名前はブラウザーのものです。',
    signedOut: 'チームはアカウントに保存されます。作成するにはログインしてください。',
    tooMany: (() => `同時に入れるチームは${LIMITS.team.perLearner}つまでです。`)(),
    teamFull: (() => `1つのチームは最大${LIMITS.team.size}人です。`)(),
    alreadyIn: 'すでにチームに入っています。',
    alreadyAsked: 'すでに招待済みです。',
    notLead: '人を招待できるのは、チームのリードだけです。',
    badName: (() => `チーム名は${LIMITS.team.name.min}〜${LIMITS.team.name.max}文字です。`)(),
    failed: '実行できませんでした。少し待ってから、もう一度お試しください。',
    privacy: 'このルームを読めるのは、あなたのチームだけです。EngineerOSの運営者も読めます。ここで報告された内容に対処するための仕組みです。',
  },
  support: {
    title: 'サポート',
    open: (reply) => (reply ? 'サポート、返信が届いています' : 'サポート'),
    signedOut: 'サポートとの会話は、アカウントに保存されます。始めるにはログインしてください。',
    closed: '現在、ここでは質問を受け付けていません。',
    loading: '読み込み中…',
    here: (n) =>
      n === 0
        ? '今は誰も回答していません。それでも質問を残してください。必ず読まれます。'
        : `今、${n}人が質問に回答しています。`,
    about: 'どんな内容ですか？',
    topics: {
      access: 'アクセスできない',
      billing: 'お支払い',
      tasks: '有償タスクとAIクレジット',
      content: 'レベルの内容がおかしい',
      bug: '不具合',
      other: 'その他',
    },
    subjectLabel: '内容を一行で(任意)',
    start: '会話を始める',
    yours: 'あなたの会話',
    stateOpen: 'オープン',
    stateResolved: 'クローズ',
    waiting: 'まだ誰も回答していません。',
    replied: '返信が届いています。',
    withAgent: (name) => [name, 'が担当しています。'],
    unclaimed: 'まだ誰も担当していません。',
    opened: (date) => `${date}に開始`,
    openRoom: '会話を開く',
    openRoomWhy: 'パネルで開き、コースの中を移動しても、そのまま表示し続けます。',
    resolveIt: '解決しました',
    resolveWhy: '会話を閉じます。もう一度書き込むと、再び開きます。',
    past: '閉じた会話',
    busy: (() => `会話を始められるのは、1日${SUPPORT_LIMITS.perDay}件までです。明日また試してください。`)(),
    failed: '実行できませんでした。少し待ってから、もう一度お試しください。',
    privacy: 'この会話の内容は、EngineerOSの運営者がすべて読みます。回答するのがその人たちだからです。他の学習者には見えません。',
  },
  rooms: {
    label: (room) => roomLabel(room, 'ja'),
    change: 'ルームを変更',
    follows: '連動',
    followsWhy: 'このルームは、開いているレベルに連動します',
    number: 'ルーム',
    range: (min, max) => `${min}–${max}`,
    go: '移動',
    thisLevel: 'このレベル',
    thisLevelWhy: '開いているレベルに連動する',
    recent: '最近',
  },
  name: {
    pick: '名前を決める',
    change: '名前を変更',
    join: '参加',
    joinRoom: (room) => `${roomLabel(room, 'ja')}に参加`,
    save: '保存',
    cancel: 'キャンセル',
    inlineNote: '参加するには、名前を決めてください。それまでは閲覧のみです。',
    note: '書き込んだ内容の横に、このブラウザーだけが持つタグとともに表示されます。アカウントは不要です。',
  },
  signIn: {
    title: '投稿するにはログイン',
    note: (room) => `${roomLabel(room, 'ja')}を閲覧中です。ログインすると、すべてのルーム、すべてのデバイスで、同じ名前と同じタグが使えます。`,
    button: 'ログイン',
    busy: 'ログイン中…',
    why: 'あなたのアカウントで投稿します。ここでも、他のブラウザーでも同じです',
    verified: 'アカウントでログイン済み',
    verifiedShort: 'ログイン済み',
  },
  faces: {
    label: '顔',
    byDefault: 'デフォルト',
    useDefault: (face) => `タグから決まる顔を使う(${face})`,
  },
  emoji: {
    search: '検索',
    searchLabel: '絵文字を検索',
    groups: 'グループ',
    results: '検索結果',
    recent: '最近',
    none: '一致するものがありません。',
    group: (g) => groupName(g, 'ja'),
    name: (e) => customName(e, 'ja'),
    cell: (e) => `${customName(e, 'ja')} — ${ltr(e.c)}`,
  },
  dock: {
    toggle: (open, unread, mentions) => jaToggle(open, unread, mentions),
    toggleWhy: (open, unread, mentions) => `${jaToggle(open, unread, mentions)}(${CHAT_SHORTCUT})`,
    panel: 'チャット',
    width: 'チャットの幅',
    height: 'チャットの高さ',
    resize: 'ドラッグしてサイズ変更',
    resizeReset: `ドラッグしてサイズ変更 · ${ltr('Home')}でリセット`,
  },
  rules: {
    name: () => nameRule('ja'),
    text: () => textRule('ja'),
    notYours: '変更できるのは、このブラウザーから送信したメッセージだけです。',
  },
};
